// IE Hub - Checkbox Field Component
// Enhanced checkbox input with validation and styling

import React, { forwardRef, useState } from 'react';
import {
  Box,
  Checkbox as MuiCheckbox,
  FormControlLabel,
  FormHelperText,
  Typography,
  alpha,
  styled,
  Switch,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  CheckBox as CheckBoxIcon,
  CheckBoxOutlineBlank as CheckBoxOutlineBlankIcon,
  IndeterminateCheckBox as IndeterminateCheckBoxIcon
} from '@mui/icons-material';

interface ValidationRules {
  required?: boolean;
  requiredMessage?: string;
}

interface CheckboxProps {
  type?: 'checkbox' | 'switch';
  label?: string | React.ReactNode;
  value?: boolean;
  onChange?: (value: boolean, event?: React.ChangeEvent<HTMLInputElement>) => void;
  validation?: ValidationRules;
  onValidate?: (isValid: boolean, error: string) => void;
  error?: boolean;
  helperText?: string;
  disabled?: boolean;
  size?: 'small' | 'medium' | 'large';
  color?: 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning' | 'default';
  placement?: 'end' | 'start' | 'top' | 'bottom';
  sx?: Record<string, unknown>;
  indeterminate?: boolean;
  variant?: 'standard' | 'soft' | 'outlined';
  [key: string]: any;
}

// Custom styled components
const StyledFormControlLabel = styled(FormControlLabel)(({ theme }) => ({
  margin: 0,
  '&:hover': {
    '& .MuiCheckbox-root, & .MuiRadio-root': {
      backgroundColor: alpha(theme.palette.primary.main, 0.08),
    },
  },
  '& .MuiFormControlLabel-label': {
    userSelect: 'none',
  },
}));

const StyledCheckbox = styled(MuiCheckbox)(({ theme }) => ({
  padding: theme.spacing(0.75),
  borderRadius: theme.shape.borderRadius,
  transition: 'all 0.2s ease-in-out',
  '&:hover': {
    backgroundColor: alpha(theme.palette.primary.main, 0.08),
  },
  '&.Mui-checked': {
    '& .MuiSvgIcon-root': {
      animation: 'checkboxCheck 0.2s ease-in-out',
    },
  },
  '@keyframes checkboxCheck': {
    '0%': {
      transform: 'scale(0.8)',
    },
    '50%': {
      transform: 'scale(1.1)',
    },
    '100%': {
      transform: 'scale(1)',
    },
  },
}));


const StyledSwitch = styled(Switch)(({ theme }) => ({
  padding: 8,
  '& .MuiSwitch-track': {
    borderRadius: 22 / 2,
    backgroundColor: theme.palette.mode === 'light' ? '#E9E9EA' : '#39393D',
    transition: theme.transitions.create(['background-color'], {
      duration: 200,
    }),
  },
  '& .MuiSwitch-thumb': {
    boxShadow: 'none',
    width: 16,
    height: 16,
    margin: 2,
    transition: theme.transitions.create(['transform'], {
      duration: 200,
    }),
  },
}));


const Checkbox = forwardRef<HTMLInputElement, CheckboxProps>(({
  type = 'checkbox',
  label,
  value = false,
  onChange,
  validation = {},
  onValidate,
  error = false,
  helperText,
  disabled = false,
  size = 'medium',
  color = 'primary',
  placement = 'end',
  sx = {},
  indeterminate = false,
  variant = 'standard',
  ...props
}, ref) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [validationError, setValidationError] = useState('');

  // Validation logic
  const validateInput = (inputValue: boolean) => {
    let errorMsg = '';

    if (validation.required && !inputValue) {
      errorMsg = validation.requiredMessage || 'This field is required';
    }

    setValidationError(errorMsg);

    if (onValidate) {
      onValidate(errorMsg === '', errorMsg);
    }

    return errorMsg === '';
  };

  // Handle checkbox/switch change
  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const checked = event.target.checked;

    // Validate on change
    if (Object.keys(validation).length > 0) {
      validateInput(checked);
    }

    if (onChange) {
      onChange(checked, event);
    }
  };

  const hasError = !!validationError || error;
  const displayHelperText = validationError || helperText;

  // Custom icon props for checkbox
  const checkboxIconProps = {
    icon: <CheckBoxOutlineBlankIcon />,
    checkedIcon: <CheckBoxIcon />,
    indeterminateIcon: <IndeterminateCheckBoxIcon />,
  };

  return (
    <Box sx={{ display: 'inline-flex', flexDirection: 'column', ...sx }}>
      <StyledFormControlLabel
        control={
          type === 'switch' ? (
            <StyledSwitch
              ref={ref as any}
              checked={!!value}
              onChange={handleChange}
              size={size}
              color={hasError ? 'error' : color}
              disabled={disabled}
              {...props}
            />
          ) : (
            <StyledCheckbox
              ref={ref as any}
              checked={!!value}
              onChange={handleChange}
              size={size}
              color={hasError ? 'error' : color}
              disabled={disabled}
              indeterminate={indeterminate}
              {...checkboxIconProps}
              {...props}
            />
          )
        }
        label={
          label ? (
            typeof label === 'string' ? (
              <Typography
                variant="body2"
                sx={{
                  color: disabled ? 'text.disabled' : hasError ? 'error.main' : 'text.primary',
                  fontWeight: variant === 'soft' && value ? 500 : 400,
                  fontSize: isMobile ? '14px' : '16px',
                  lineHeight: isMobile ? '20px' : '22px',
                }}
              >
                {label}
                {validation.required && <span style={{ color: '#d32f2f' }}> *</span>}
              </Typography>
            ) : (
              label
            )
          ) : undefined
        }
        labelPlacement={placement}
        sx={{
          '& .MuiFormControlLabel-label': {
            ml: placement === 'end' ? 0.5 : 0,
            mr: placement === 'start' ? 0.5 : 0
          }
        }}
      />
      {displayHelperText && (
        <FormHelperText
          error={hasError}
          sx={{
            mt: 0.5,
            ml: type === 'switch' ? 6 : (size === 'small' ? 3.5 : size === 'medium' ? 4.5 : 5.5)
          }}
        >
          {displayHelperText}
        </FormHelperText>
      )}
    </Box>
  );
});

Checkbox.displayName = 'Checkbox';

export default Checkbox;